import React from "react";
import {
  DEFAULT_ISSUE_DETAILS,
  OVERRIDE_DECISIONS,
  SIMPLE_DECISIONS,
  FieldDecision,
  FormConfigField,
  WorkspaceTask,
} from "../shared/types";
import * as S from "../shared/styles";

interface FieldReviewTableProps {
  fields: FormConfigField[];
  isOverride: boolean;
  task: WorkspaceTask;
  decisions: Record<string, FieldDecision>;
  onDecide: (label: string, patch: Partial<FieldDecision>) => void;
  showError: boolean;
  disabled?: boolean;
}

const FieldReviewTable: React.FC<FieldReviewTableProps> = ({
  fields,
  isOverride,
  task,
  decisions,
  onDecide,
  showError,
  disabled = false,
}) => {
  const decisionOptions = isOverride
    ? OVERRIDE_DECISIONS.map((v) => ({
        value: v,
        label:
          v === "ai_correct"
            ? "AI Correct"
            : v === "human_correct"
              ? "Human Correct"
              : "Both Incorrect",
      }))
    : SIMPLE_DECISIONS.map((v) => ({
        value: v,
        label: v === "correct" ? "Correct" : "Incorrect",
      }));

  let lastQuestion: string | null = null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0px" }}>
      {fields.map((f) => {
        const d = decisions[f.label] || {};
        const needsCorrection = d.decision === "incorrect" || d.decision === "both_incorrect";
        const missingDecision = f.mandatory && !d.decision;
        const missingIssue = needsCorrection && !d.issue_detail;
        const missingExplanation = needsCorrection && !d.correction_value;
        const hasError = showError && (missingDecision || missingIssue || missingExplanation);
        const issueOpts =
          f.options.filter(Boolean).length > 0
            ? f.options.filter(Boolean)
            : [...DEFAULT_ISSUE_DETAILS];

        const question = f.question?.trim() || null;
        const showQuestion = question !== null && question !== lastQuestion;
        if (question !== null) lastQuestion = question;
        else lastQuestion = null;

        return (
          <React.Fragment key={f.id}>
            {showQuestion && <div style={questionBanner}>{question}</div>}
            <div style={{ ...fieldCard, ...(hasError ? { borderColor: "#fca5a5" } : {}) }}>
              {/* -- Row 1: field label + LLM extracted (full width) -- */}
              <div style={fieldHeader}>
                <span style={{ ...fieldLabel, color: hasError ? "#ef4444" : "#374151" }}>
                  {f.label}
                  {f.mandatory && <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>}
                </span>
              </div>

              <div style={valuesRow}>
                <div style={valueBlock}>
                  <div style={valueBox}>{task.ai_generated_values?.[f.label] ?? "\u2014"}</div>
                </div>
                {isOverride && (
                  <div style={valueBlock}>
                    <div style={{ ...valueLabel, color: "#b45309" }}>Austin output</div>
                    <div style={{ ...valueBox, background: "#fef3c7", borderColor: "#fde68a" }}>
                      {task.human_override_values?.[f.label] ?? "\u2014"}
                    </div>
                  </div>
                )}
              </div>

              {/* -- Row 2: Review decision -- */}
              <div style={decisionRow}>
                <div style={decisionLabel}>Review decision</div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {decisionOptions.map((o) => {
                    const selected = d.decision === o.value;
                    return (
                      <label
                        key={o.value}
                        style={{
                          ...pillLabel,
                          ...(selected ? pillSelected : pillDefault),
                          ...(disabled ? { cursor: "default", opacity: 0.7 } : {}),
                          ...(missingDecision && showError && !selected
                            ? { borderColor: "#fca5a5" }
                            : {}),
                        }}
                      >
                        <input
                          type="radio"
                          name={`decision-${f.id}`}
                          value={o.value}
                          checked={selected}
                          disabled={disabled}
                          onChange={() => {
                            const nextNeedsCorrection =
                              o.value === "incorrect" || o.value === "both_incorrect";
                            onDecide(
                              f.label,
                              nextNeedsCorrection
                                ? { decision: o.value }
                                : {
                                    decision: o.value,
                                    issue_detail: undefined,
                                    correction_value: undefined,
                                  },
                            );
                          }}
                          style={{ display: "none" }}
                        />
                        {o.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* -- Row 3: Issue details (only when Incorrect) -- */}
              {needsCorrection && (
                <div style={issueSection}>
                  <fieldset
                    style={{
                      border: missingIssue && showError ? "1px solid #ef4444" : "1px solid #e5e7eb",
                      borderRadius: "6px",
                      padding: "8px 10px",
                      margin: 0,
                    }}
                  >
                    <legend
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: missingIssue && showError ? "#ef4444" : "#6b7280",
                        padding: "0 4px",
                      }}
                    >
                      Issue detail
                    </legend>
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      {issueOpts.map((o) => (
                        <label
                          key={o}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "6px",
                            padding: "3px 0",
                            fontSize: "0.8125rem",
                            color: "#374151",
                            cursor: disabled ? "default" : "pointer",
                            lineHeight: 1.4,
                          }}
                        >
                          <input
                            type="radio"
                            name={`issue-${f.id}`}
                            value={o}
                            checked={d.issue_detail === o}
                            disabled={disabled}
                            onChange={() => onDecide(f.label, { issue_detail: o })}
                            style={{ marginTop: "3px", flexShrink: 0 }}
                          />
                          <span style={{ wordBreak: "break-word" }}>{o}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <textarea
                    value={d.correction_value ?? ""}
                    disabled={disabled}
                    onChange={(e) => onDecide(f.label, { correction_value: e.target.value })}
                    placeholder="Explain evidence and the correct interpretation"
                    rows={2}
                    style={{
                      ...S.formInput,
                      borderColor: missingExplanation && showError ? "#ef4444" : "#d1d5db",
                      resize: "vertical" as const,
                      minHeight: "48px",
                    }}
                  />
                </div>
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ----------------------------  Styles  ----------------------------

const questionBanner: React.CSSProperties = {
  padding: "10px 14px",
  fontWeight: 600,
  fontSize: "13px",
  color: "#1e3a5f",
  background: "#f0f7ff",
  borderRadius: "6px 6px 0 0",
  border: "1px solid #bfdbfe",
  borderBottom: "none",
};

const fieldCard: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderRadius: "6px",
  padding: "14px 16px",
  marginBottom: "8px",
  background: "white",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
};

const fieldHeader: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const fieldLabel: React.CSSProperties = {
  fontWeight: 600,
  fontSize: "0.875rem",
};

const valuesRow: React.CSSProperties = {
  display: "flex",
  gap: "12px",
};

const valueBlock: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const valueLabel: React.CSSProperties = {
  fontSize: "0.7rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#0f7b8a",
  marginBottom: "4px",
};

const valueBox: React.CSSProperties = {
  background: "#f0fdfa",
  border: "1px solid #ccfbf1",
  borderRadius: "6px",
  padding: "10px 12px",
  fontSize: "0.8125rem",
  lineHeight: 1.6,
  color: "#1f2937",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: "300px",
  overflowY: "auto",
};

const decisionRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const decisionLabel: React.CSSProperties = {
  fontSize: "0.75rem",
  fontWeight: 600,
  color: "#6b7280",
  flexShrink: 0,
};

const pillLabel: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "5px 14px",
  borderRadius: "16px",
  fontSize: "0.8125rem",
  fontWeight: 500,
  cursor: "pointer",
  transition: "all 0.15s",
  border: "1px solid transparent",
  userSelect: "none",
};

const pillDefault: React.CSSProperties = {
  background: "#f3f4f6",
  color: "#4b5563",
  borderColor: "#d1d5db",
};

const pillSelected: React.CSSProperties = {
  background: "#0f7b8a",
  color: "white",
  borderColor: "#0f7b8a",
};

const issueSection: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  paddingTop: "4px",
  borderTop: "1px solid #f3f4f6",
};

export default FieldReviewTable;
